import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Skeleton } from 'antd';
import { useGlobalState } from '../../common/GlobalState';
import { useHistory } from 'react-router-dom';
import ResultsMap from './components/ResultsMap';
import MapQuickFilters from './components/MapFilters';
import ResultsList from './components/ResultsList';
import './assets/css/search.css';
import SearchBox from '../../common/SearchBox';
import HeaderMenu from '../../common/components/menu/HeaderMenu';
import MapIconCircle from '../../../assets/images/Icons/SVG/MapIconCircle';

import { useGetSearchResultsMutation } from './searchSlice';

import ResetSearchButton from './components/ResetSearchButton';
import { useQuery } from '../../../utils/hooks';

const INITIAL_FILTERS = {
  is_free: false,
  is_children_allowed: false,
  is_wheelchair_accessible: false,
  has_rod_licence_waiver: false,
  is_adults_only: false,
  is_cast_award: false,
  event_type_ids: [],
  disciplines: [],
};

export default function Search() {
  let paramQuery = useQuery();
  const [, setSearchBoxContents] = useGlobalState('searchBoxContents');
  let _query = paramQuery.get('query');

  const history = useHistory();

  const loginLoadingState = useState(false);
  const isLoggedInState = useState(false);

  const [showMap, setShowMap] = useState(false);

  const [usingMapBounds, setUsingMapBounds] = useState(false);

  // Search Variables
  const mapRef = useRef(null);
  const [bounds, setBounds] = useState(null);
  const [nw_lng = null, se_lat = null, se_lng = null, nw_lat = null] =
    bounds || [];

  const [bookableOnly, setBookableOnly] = useState(false);

  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const setSearchFilter = useCallback(
    (filterName, value) =>
      setFilters((prev) => ({
        ...prev,
        [filterName]:
          typeof value === 'function' ? value(prev?.[filterName]) : value,
      })),
    [setFilters]
  );
  const hasQueryTerm = Boolean(_query);
  const isFiltered = Object.values(filters).some(
    (f) => f === true || f?.length
  );
  const showReset =
    hasQueryTerm || isFiltered || usingMapBounds || bookableOnly;

  let searchQueryTerm = _query ?? '';

  const resetSearchAndFilters = useCallback(() => {
    history.replace('/events/search');
    setSearchBoxContents(null);
    setUsingMapBounds(false);
    setFilters(INITIAL_FILTERS);
    setBookableOnly(false);
    mapRef?.current?.handleReset?.();
  }, [history, setSearchBoxContents]);

  const [
    getV2SearchResults,
    { data: searchResults, isLoading: loading, isError: searchError },
  ] = useGetSearchResultsMutation();

  const noResults = !searchResults?.length;

  const searchBounds = useMemo(
    () =>
      usingMapBounds
        ? {
            top_left: {
              lat: nw_lat,
              lng: nw_lng,
            },
            bottom_right: {
              lat: se_lat,
              lng: se_lng,
            },
          }
        : null,
    [nw_lat, nw_lng, se_lat, se_lng, usingMapBounds]
  );

  useEffect(() => {
    getV2SearchResults({
      term: usingMapBounds ? null : searchQueryTerm,
      bounds: searchBounds,
      include_bookable: true,
      include_directory: !bookableOnly,
      filters: filters,
    })
      .unwrap()
      .then(() => {
        mapRef?.current?.autoFit?.();
      });
  }, [
    bookableOnly,
    filters,
    searchBounds,
    getV2SearchResults,
    searchQueryTerm,
    usingMapBounds,
  ]);

  const titleFound = loading ? (
    <Skeleton
      active
      size={'small'}
      paragraph={false}
      style={{ height: 'calc(1em + 8px)' }}
    />
  ) : (
    `${searchResults?.length ?? 0}${
      searchResults?.length > 30 ? '+' : ''
    } Events found`
  );
  const titleLocation = `Results ${
    usingMapBounds
      ? 'in Selected Map Area'
      : _query
        ? `for "${_query}"`
        : `for All Locations`
  }`;

  console.log(loading);
  return (
    <>
      <div className='search-layout'>
        <div className={'search-hamburger search-hamburger--desktop'}>
          <HeaderMenu
            loadingState={loginLoadingState}
            isLoggedInState={isLoggedInState}
            triggerRedirect={() => {}}
          />
        </div>
        <div className='search-controls pd-left-16 pd-right-16 pd-top-20'>
          <div className='search-controls-top w-full mg-bottom-15 row-center'>
            <SearchBox
              className={'primary-search-box'}
              onSearch={() => setUsingMapBounds(false)}
              isEventSearch={true}
            />

            <div className={'search-hamburger search-hamburger--mobile'}>
              <HeaderMenu
                loadingState={loginLoadingState}
                isLoggedInState={isLoggedInState}
                triggerRedirect={() => {}}
              />
            </div>
          </div>
          <div className='search-title--desktop mg-bottom-20'>
            <div className='justify-between row-center gap-1r'>
              <div>
                <p className='fs-26 gSemiBold mg-bottom-4 sb-blue-900 lh-1.25'>
                  {titleLocation}
                </p>
                <p className='grey-700 gRegular mg-bottom-0'>{titleFound}</p>
              </div>
            </div>
          </div>
          <div className='search-title--mobile mg-top-4 mg-bottom-12 pd-left-5 pd-right-5'>
            <div className='justify-between row-center gap-05r'>
              <div>
                <p className='fs-16 gSemiBold mg-bottom-0 sb-blue-900 lh-1.25 mg-bottom-8'>
                  {titleLocation}
                </p>
                <p className='grey-700 gMedium mg-bottom-0 '>{titleFound}</p>
              </div>
            </div>
          </div>
          <div className={'quick-links-desktop'}>
            <div className='pd-top-15 pd-bottom-15 results-quick-filters '>
              <MapQuickFilters
                selected={filters}
                onSelectionChange={(type, values) =>
                  setSearchFilter(type, values)
                }
              />
            </div>
          </div>
        </div>
        <div className='search-results'>
          <div
            className={`search-drawer results-list-drawer ${
              !showMap ? 'results-list-drawer--open' : ''
            }`}
            onClick={(e) => {
              e.preventDefault();
              setShowMap(false);
            }}
          >
            <p
              className='grey-700 open-drawer-text gSemiBold fs-18 txt-center w-full'
              onClick={() => setShowMap(false)}
            >
              View list of results ({searchResults?.length ?? 0})
            </p>
            <div id='results-list-mobile' className='pd-bottom-100 grey-bg-100'>
              <ResultsList
                results={searchResults}
                scrollableTargetId={'results-list-mobile'}
                loading={loading}
                error={searchError}
                onReset={resetSearchAndFilters}
              />
            </div>
          </div>

          <div
            id={'results-list-desktop'}
            className='results-list-desktop pd-bottom-40 grey-bg-100'
          >
            <ResultsList
              results={searchResults}
              scrollableTargetId={'results-list-desktop'}
              loading={loading}
              error={searchError}
              onReset={resetSearchAndFilters}
            />
          </div>

          <div className='floating-button-container floating-button-container--bottom-center'>
            {!showMap && (
              <div className='display-flex-column gap-05r'>
                {showReset && (
                  <ResetSearchButton
                    className='floating-button resetSearchButtonMobile'
                    reset={resetSearchAndFilters}
                  />
                )}
                {!searchError && !noResults && (
                  <button
                    className='floating-button showMapButton gSemiBold white fs-16'
                    onClick={() => setShowMap(true)}
                  >
                    View Map <MapIconCircle className={'sb-blue'} />
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        <div className='search-map'>
          <ResultsMap
            ref={mapRef}
            bounds={bounds}
            setBounds={setBounds}
            usingBounds={usingMapBounds}
            setUsingBounds={setUsingMapBounds}
            results={searchResults}
            isInitialResults={!hasQueryTerm && !isFiltered}
            showReset={showReset}
            bookableOnly={bookableOnly}
            onBoundsEnableChange={(enabled) => {
              if (enabled) {
                setSearchBoxContents(null);
              } else {
                setSearchBoxContents(_query);
              }
            }}
            resetSearchAndFilters={resetSearchAndFilters}
          />
        </div>
      </div>
    </>
  );
}
